"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/app/_module/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const [indicatorStyle, setIndicatorStyle] = React.useState<React.CSSProperties>({
    opacity: 0,
  });
  const [isInitialized, setIsInitialized] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  const uniqueId = React.useId();

  const updateIndicator = React.useCallback((skipTransition = false) => {
    const list = listRef.current;
    if (!list) return;

    // Find the active tab within THIS list only
    const activeTab = list.querySelector('[data-state="active"][role="tab"]') as HTMLElement;

    if (activeTab) {
      const listRect = list.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      // Account for scroll position when calculating the indicator position
      const scrollLeft = list.scrollLeft;
      const offsetLeft = tabRect.left - listRect.left + scrollLeft;

      setIndicatorStyle({
        width: tabRect.width,
        height: tabRect.height,
        transform: `translateX(${offsetLeft}px)`,
        opacity: 1,
      });

      // Mark as initialized after first position is set
      if (skipTransition) {
        // Use requestAnimationFrame to ensure the position is applied before enabling transitions
        requestAnimationFrame(() => {
          setIsInitialized(true);
        });
      }
    }
  }, []);

  // Update on mount and when children change
  React.useEffect(() => {
    // Initial update with delay for DOM to settle, skip transition on first render
    const timer = setTimeout(() => updateIndicator(true), 50);
    return () => clearTimeout(timer);
  }, [updateIndicator]);

  // Watch for changes in the list
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const observer = new MutationObserver((mutations) => {
      // Check if any mutation involves data-state changing to active
      const hasActiveChange = mutations.some(
        (m) => m.attributeName === 'data-state'
      );
      if (hasActiveChange) {
        updateIndicator();
      }
    });

    observer.observe(list, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-state'],
    });

    return () => observer.disconnect();
  }, [updateIndicator]);

  // Update on resize with debounce for smoother handling
  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    let rafId: number;

    const handleResize = () => {
      // Cancel any pending updates
      clearTimeout(resizeTimeout);
      cancelAnimationFrame(rafId);

      // Use RAF for immediate visual update during resize
      rafId = requestAnimationFrame(() => {
        updateIndicator();
      });

      // Also schedule a follow-up update after resize settles
      resizeTimeout = setTimeout(() => {
        updateIndicator();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Also use ResizeObserver to catch container size changes
    const list = listRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (list && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        rafId = requestAnimationFrame(() => {
          updateIndicator();
        });
      });
      resizeObserver.observe(list);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [updateIndicator]);

  // Update on scroll (for horizontally scrollable tabs)
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const handleScroll = () => updateIndicator();
    list.addEventListener('scroll', handleScroll);
    return () => list.removeEventListener('scroll', handleScroll);
  }, [updateIndicator]);

  return (
    <TabsPrimitive.List
      ref={(node) => {
        (listRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn(
        "h-12 items-center rounded-lg bg-muted p-1.5 text-muted-foreground relative overflow-visible",
        // Use flex by default, but if grid or flex classes are passed, they'll override
        !className?.includes('grid') && !className?.includes('flex') && "inline-flex",
        className
      )}
      {...props}
    >
      {/* Sliding indicator - scoped to this list only */}
      <div
        data-indicator-id={uniqueId}
        className="absolute top-1.5 left-0 bg-background rounded-md border-2 border-black shadow-lg pointer-events-none"
        style={{
          ...indicatorStyle,
          // Only apply transitions after initial position is set to prevent slide-in animation on page load
          transition: isInitialized
            ? 'transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1), width 200ms cubic-bezier(0.25, 0.1, 0.25, 1), opacity 150ms ease'
            : 'none',
        }}
      />
      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative z-10",
      "data-[state=active]:text-foreground",
      "data-[state=inactive]:hover:text-foreground/80",
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
